import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useLocation, useNavigate } from 'react-router-dom';

// This is the new Menu Overlay component
const MenuOverlay = ({ onClose }) => {
  const location = useLocation();
  const navigate = useNavigate();
  
  const overlayVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { duration: 0.3 } },
    exit: { opacity: 0, transition: { duration: 0.3 } },
  };

  const linkStyle = {
    fontFamily: 'PPNeueMontreal, sans-serif',
    fontSize: 'clamp(2rem, 8vw, 5rem)', // Responsive font size
    color: '#333333',
    textDecoration: 'none',
    lineHeight: 1.2,
    transition: 'color 0.2s ease',
  };

  const linkHoverStyle = {
    ...linkStyle,
    color: '#000000',
  };

  const handleNavigation = (sectionId) => {
    // If we're not on the home page, navigate there first
    if (location.pathname !== '/') {
      navigate('/');
      // Wait a bit for the page to load, then scroll to section
      setTimeout(() => {
        const element = document.getElementById(sectionId);
        if (element) {
          const headerHeight = 92.5;
          const elementPosition = element.offsetTop - headerHeight;
          window.scrollTo({
            top: elementPosition,
            behavior: 'smooth'
          });
        }
      }, 100);
    } else {
      // If we're already on the home page, just scroll to section
      const element = document.getElementById(sectionId);
      if (element) {
        const headerHeight = 92.5;
        const elementPosition = element.offsetTop - headerHeight;
        window.scrollTo({
          top: elementPosition,
          behavior: 'smooth'
        });
      }
    }
    onClose();
  };

  return (
    <motion.div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100vw',
        height: '100vh',
        backgroundColor: '#ffffff',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 90, // Just below the header buttons
      }}
      variants={overlayVariants}
      initial="hidden"
      animate="visible"
      exit="exit"
    >
      <ul style={{ listStyle: 'none', padding: 0, textAlign: 'center' }}>
        <li style={{ marginBottom: '20px' }}>
          <button 
            onClick={() => handleNavigation('agentur')}
            style={{
              ...linkStyle,
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: 0,
            }}
            onMouseEnter={(e) => e.target.style.color = linkHoverStyle.color}
            onMouseLeave={(e) => e.target.style.color = linkStyle.color}
          >
            Agentur
          </button>
        </li>
        <li style={{ marginBottom: '20px' }}>
          <button 
            onClick={() => handleNavigation('projekte')}
            style={{
              ...linkStyle,
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: 0,
            }}
            onMouseEnter={(e) => e.target.style.color = linkHoverStyle.color}
            onMouseLeave={(e) => e.target.style.color = linkStyle.color}
          >
            Projekte
          </button>
        </li>
        <li style={{ marginBottom: '20px' }}>
          <button 
            onClick={() => handleNavigation('services')}
            style={{
              ...linkStyle,
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: 0,
            }}
            onMouseEnter={(e) => e.target.style.color = linkHoverStyle.color}
            onMouseLeave={(e) => e.target.style.color = linkStyle.color}
          >
            Services
          </button>
        </li>
        <li>
          <button 
            onClick={() => handleNavigation('kontakt')}
            style={{
              ...linkStyle,
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: 0,
            }}
            onMouseEnter={(e) => e.target.style.color = linkHoverStyle.color}
            onMouseLeave={(e) => e.target.style.color = linkStyle.color}
          >
            Kontakt
          </button>
        </li>
      </ul>
    </motion.div>
  );
};

// Hamburger Menu Icon Component
const HamburgerIcon = ({ isOpen }) => {
  const lineStyle = {
    width: '24px',
    height: '2px',
    backgroundColor: '#111111',
    transition: 'all 0.3s ease',
    pointerEvents: 'none', // Prevent icon from interfering with button clicks
  };

  return (
    <div style={{ 
      display: 'flex', 
      flexDirection: 'column', 
      alignItems: 'center', 
      justifyContent: 'center',
      pointerEvents: 'none', // Prevent icon from interfering with button clicks
      position: 'relative',
      width: '24px',
      height: '20px', // Increased height to accommodate the lines
    }}>
      <div 
        style={{
          ...lineStyle,
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: isOpen 
            ? 'translate(-50%, -50%) rotate(45deg)' 
            : 'translate(-50%, -50%) translateY(-7px)',
        }}
      />
      <div 
        style={{
          ...lineStyle,
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          opacity: isOpen ? 0 : 1,
        }}
      />
      <div 
        style={{
          ...lineStyle,
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: isOpen 
            ? 'translate(-50%, -50%) rotate(-45deg)' 
            : 'translate(-50%, -50%) translateY(7px)',
        }}
      />
    </div>
  );
};

export const Header = () => {
  const navigate = useNavigate();
  // State to control the menu's visibility
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== 'undefined' ? window.matchMedia('(max-width: 600px)').matches : false
  );

  // Synchronously derive mobile breakpoint via matchMedia to avoid initial flash
  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return;
    const mediaQuery = window.matchMedia('(max-width: 600px)');
    const handleChange = (e) => setIsMobile(e.matches);
    // Ensure state is correct on mount
    setIsMobile(mediaQuery.matches);
    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  // --- Styles ---
  const headerStyle = {
    position: 'fixed',
    flexDirection: 'row-reverse',
    top: 0,
    left: 0,
    width: '100%',
    padding: isMobile ? '20px 24px' : '20px 80px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    zIndex: 100,
    boxSizing: 'border-box',
  };

  const logoStyle = {
    fontFamily: 'PPNeueMontreal, sans-serif',
    fontWeight: '500',
    fontSize: '18px',
    color: '#111111',
    zIndex: 101, // Ensure logo is above the overlay
    display: isMobile ? 'none' : 'block',
    cursor: 'pointer',
    transition: 'color 0.2s ease',
  };

  const navStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    zIndex: 101, // Ensure nav is above the overlay
  };
  
  // Updated with larger padding and font size
  const buttonBaseStyle = {
    fontFamily: 'PPNeueMontreal, sans-serif',
    padding: isMobile ? '12px 20px' : '16px 32px',
    borderRadius: '100px',
    textDecoration: 'none',
    fontSize: isMobile ? '16px' : '18px',
    fontWeight: '500',
    transition: 'all 0.3s ease',
    border: 'none',
    cursor: 'pointer',
    transform: 'translateY(0)',
    border: '1px solid #111111',
  };

  const contactButtonStyle = {
    ...buttonBaseStyle,
    backgroundColor: '#111111',
    color: '#ffffff',
    display: isMobile ? 'none' : 'block',
  };

  const contactButtonHoverStyle = {
    ...contactButtonStyle,
    backgroundColor: '#FFFFFF',
    color: '#111111',
  };

  const menuButtonStyle = {
    ...buttonBaseStyle,
    backgroundColor: '#FFFFFF',
    color: '#111111',
    display: isMobile ? 'none' : 'block',
  };

  const menuButtonHoverStyle = {
    ...menuButtonStyle,
    backgroundColor: '#000000',
    color: '#FFFFFF',
  };

  // Mobile menu button style
  const mobileMenuButtonStyle = {
    ...buttonBaseStyle,
    backgroundColor: '#ffffff',
    color: '#000000',
    padding: '12px',
    minWidth: '48px',
    minHeight: '48px',
    display: isMobile ? 'flex' : 'none',
    alignItems: 'center',
    justifyContent: 'center',
  };

  const mobileMenuButtonHoverStyle = {
    ...mobileMenuButtonStyle,
    backgroundColor: '#ffffff',
    color: '#000000',
  };

  return (
    <>
      <header style={headerStyle}>
        <nav style={navStyle}>
          {!isMobile && (
            <>
              {/* Desktop Contact Button */}
              <a 
                href="mailto:info@bubbling.ch" 
                style={contactButtonStyle}
                onMouseEnter={(e) => {
                  Object.assign(e.target.style, contactButtonHoverStyle);
                }}
                onMouseLeave={(e) => {
                  Object.assign(e.target.style, contactButtonStyle);
                }}
              >
                Kontakt
              </a>
              {/* Desktop Menu Button */}
              <button 
                onClick={toggleMenu} 
                style={menuButtonStyle}
                onMouseEnter={(e) => {
                  Object.assign(e.target.style, menuButtonHoverStyle);
                }}
                onMouseLeave={(e) => {
                  Object.assign(e.target.style, menuButtonStyle);
                }}
              >
                {isMenuOpen ? 'Schliessen X' : 'Menü ••'}
              </button>
            </>
          )}
          {isMobile && (
            <button 
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                toggleMenu();
              }}
              style={mobileMenuButtonStyle}
              onMouseEnter={(e) => {
                Object.assign(e.target.style, mobileMenuButtonHoverStyle);
              }}
              onMouseLeave={(e) => {
                Object.assign(e.target.style, mobileMenuButtonStyle);
              }}
              type="button"
            >
              <HamburgerIcon isOpen={isMenuOpen} />
            </button>
          )}
        </nav>
        {!isMobile && (
          <div 
            style={logoStyle}
            onClick={() => {
              if (location.pathname === '/') {
                // If already on home page, scroll to top
                window.scrollTo({
                  top: 0,
                  behavior: 'smooth'
                });
              } else {
                // If on another page, navigate to home
                navigate('/');
              }
            }}
            onMouseEnter={(e) => {
              e.target.style.color = '#0010CA';
            }}
            onMouseLeave={(e) => {
              e.target.style.color = '#111111';
            }}
          >
            AGENTUR
          </div>
        )}
      </header>
      <AnimatePresence>
        {isMenuOpen && <MenuOverlay onClose={toggleMenu} />}
      </AnimatePresence>
    </>
  );
};